/**
 * Helpers / subcomponents for MachineManagementInner complexity reduction.
 */

import React from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, Plus, Search, WifiOff } from 'lucide-react';
import { DataTable, EmptyState, Pagination, TableColumn } from '../../components/common';
import { ICON_STROKE_WIDTH } from '../../constants';
import type {
  Machine,
  MachineColumnKey,
  MachineConnectivityStatus,
  MachineTableRow,
} from '../../types/machine.types';
import MachineEditPanel from './MachineEditPanel';

export const MACHINE_COLUMN_LABELS: Record<MachineColumnKey, string> = {
  machine: 'Machine',
  modality: 'Modality',
  aeTitle: 'AE title',
  ipPort: 'IP : port',
  location: 'Location',
  activeInactive: 'Active/Inactive',
  connectivityStatus: 'Status',
  lastChecked: 'Last checked',
};

export const DEFAULT_VISIBLE_COLUMNS: Record<MachineColumnKey, boolean> = {
  machine: true,
  modality: true,
  aeTitle: true,
  ipPort: true,
  location: false,
  activeInactive: true,
  connectivityStatus: true,
  lastChecked: true,
};

export function markMachinesChecking(prev: Machine[], list: Machine[]): Machine[] {
  const ids = new Set(list.map(item => item.id));
  return prev.map(m => (ids.has(m.id) ? { ...m, connectivityStatus: 'Checking' as const } : m));
}

export function replaceMachineById(prev: Machine[], updated: Machine): Machine[] {
  return prev.map(item => (item.id === updated.id ? updated : item));
}

export function isConfigurationFailure(message?: string | null): boolean {
  if (!message) {
    return false;
  }
  return /configur/i.test(message) || /misconfigur/i.test(message);
}

export function ConnectivityStatusBadge({
  status,
  responseTime,
  failureReason,
}: Readonly<{
  status: MachineConnectivityStatus;
  responseTime?: number | null;
  failureReason?: string | null;
}>) {
  if (status === 'Checking') {
    return (
      <span className="machine-connectivity-badge machine-connectivity-badge--checking">
        Checking…
      </span>
    );
  }

  if (status === 'Online') {
    return (
      <span
        className="machine-connectivity-badge machine-connectivity-badge--online"
        title={responseTime == null ? undefined : `${responseTime}ms response time`}
      >
        <CheckCircle2
          size={14}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        Online
        {responseTime == null ? null : (
          <span className="machine-connectivity-latency"> · {responseTime}ms</span>
        )}
      </span>
    );
  }

  if (status === 'ConfigurationError') {
    return (
      <span
        className="machine-connectivity-badge machine-connectivity-badge--config-error"
        title={failureReason ?? undefined}
      >
        <AlertTriangle
          size={14}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        Configuration error
      </span>
    );
  }

  if (status === 'Offline') {
    return (
      <span
        className="machine-connectivity-badge machine-connectivity-badge--offline"
        title={failureReason ?? undefined}
      >
        <WifiOff
          size={14}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        Offline
      </span>
    );
  }

  return <span className="machine-connectivity-badge machine-connectivity-badge--unknown">—</span>;
}

export function buildMachineTableColumns(
  columnVisibility: Record<MachineColumnKey, boolean>
): TableColumn<MachineTableRow>[] {
  const cols: TableColumn<MachineTableRow>[] = [];

  if (columnVisibility.machine) {
    cols.push({
      key: 'machineName',
      label: MACHINE_COLUMN_LABELS.machine,
      width: 160,
      flex: 2,
      align: 'left',
      render: (_value, row) => (
        <div>
          <div className="machine-cell-primary">{row.machineName}</div>
          {row.location ? <div className="machine-cell-secondary">{row.location}</div> : null}
        </div>
      ),
    });
  }
  if (columnVisibility.modality) {
    cols.push({
      key: 'modality',
      label: MACHINE_COLUMN_LABELS.modality,
      width: 90,
      flex: 1,
      align: 'left',
    });
  }
  if (columnVisibility.aeTitle) {
    cols.push({
      key: 'aeTitle',
      label: MACHINE_COLUMN_LABELS.aeTitle,
      width: 120,
      flex: 1,
      align: 'left',
    });
  }
  if (columnVisibility.ipPort) {
    cols.push({
      key: 'ipPort',
      label: MACHINE_COLUMN_LABELS.ipPort,
      width: 130,
      flex: 1,
      align: 'left',
    });
  }
  if (columnVisibility.location) {
    cols.push({
      key: 'location',
      label: MACHINE_COLUMN_LABELS.location,
      width: 160,
      flex: 2,
      align: 'left',
    });
  }
  if (columnVisibility.activeInactive) {
    cols.push({
      key: 'activeInactive',
      label: MACHINE_COLUMN_LABELS.activeInactive,
      width: 120,
      flex: 1,
      align: 'left',
      render: (_value, row) => (
        <span
          className={`machine-status-badge ${
            row.activeInactive === 'Active'
              ? 'machine-status-badge--active'
              : 'machine-status-badge--inactive'
          }`}
        >
          {row.activeInactive}
        </span>
      ),
    });
  }
  if (columnVisibility.connectivityStatus) {
    cols.push({
      key: 'connectivityStatus',
      label: MACHINE_COLUMN_LABELS.connectivityStatus,
      width: 110,
      flex: 1,
      align: 'left',
      render: (_value, row) => (
        <ConnectivityStatusBadge
          status={row.connectivityStatus}
          responseTime={row.responseTime}
          failureReason={row.failureReason}
        />
      ),
    });
  }
  if (columnVisibility.lastChecked) {
    cols.push({
      key: 'lastChecked',
      label: MACHINE_COLUMN_LABELS.lastChecked,
      width: 120,
      flex: 1,
      align: 'left',
      render: (_value, row) => <span className="machine-last-checked">{row.lastChecked}</span>,
    });
  }

  return cols;
}

export function resolveMachineFromConnectivityResult(
  machine: Machine,
  result: { success: boolean; data?: Machine; error?: string }
): Machine {
  if (result.success && result.data) {
    return result.data;
  }
  return {
    ...machine,
    connectivityStatus: isConfigurationFailure(result.error) ? 'ConfigurationError' : 'Offline',
    failureReason: result.error || 'Connectivity check failed',
    responseTime: null,
  };
}

export function MachineTopbarActions({
  activeTab,
  facilityName,
  onRegister,
  onScheduleMaintenance,
}: Readonly<{
  activeTab: 'imaging' | 'maintenance';
  facilityName: string;
  onRegister: () => void;
  onScheduleMaintenance: () => void;
}>) {
  let actionButton: React.ReactNode = null;
  if (activeTab === 'imaging') {
    actionButton = (
      <button
        type="button"
        className="machine-management-register-btn"
        onClick={onRegister}
      >
        <Plus
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        Add machine
      </button>
    );
  } else if (activeTab === 'maintenance') {
    actionButton = (
      <button
        type="button"
        className="machine-management-register-btn"
        onClick={onScheduleMaintenance}
      >
        <Plus
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        Schedule maintenance
      </button>
    );
  }

  return (
    <div className="machine-management-topbar-right">
      <div
        className="machine-facility-chip"
        title={facilityName}
      >
        {facilityName}
      </div>
      {actionButton}
    </div>
  );
}

export function MachineImagingTableBody({
  loading,
  error,
  tableRows,
  columns,
  debouncedSearch,
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onRowClick,
  onRegister,
}: Readonly<{
  loading: boolean;
  error: string | null;
  tableRows: MachineTableRow[];
  columns: TableColumn<MachineTableRow>[];
  debouncedSearch: string;
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onRowClick: (row: MachineTableRow) => void;
  onRegister: () => void;
}>) {
  if (loading) {
    return <p className="machine-management-empty">Loading machines…</p>;
  }
  if (error) {
    return <p className="machine-management-empty">{error}</p>;
  }
  if (tableRows.length === 0) {
    return (
      <div className="machine-management-empty-wrap">
        <EmptyState
          title="No machines registered"
          description={
            debouncedSearch
              ? 'No machines match your search.'
              : 'Register an imaging machine to get started.'
          }
          actionLabel={debouncedSearch ? undefined : 'Add machine'}
          onAction={debouncedSearch ? undefined : onRegister}
        />
      </div>
    );
  }
  return (
    <>
      <DataTable
        columns={columns as TableColumn[]}
        data={tableRows as unknown as Record<string, unknown>[]}
        onRowClick={row => onRowClick(row as unknown as MachineTableRow)}
        getRowKey={(row, index) => (row as unknown as MachineTableRow).id ?? index}
        pageScrollStickyHeader
        fullWidth
      />
      {totalItems > itemsPerPage ? (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      ) : null}
    </>
  );
}

export function MachineImagingTabBody({
  selectedMachine,
  searchQuery,
  setSearchQuery,
  columnsOpen,
  setColumnsOpen,
  columnsRef,
  toggleableColumns,
  columnVisibility,
  setColumnVisibility,
  loading,
  error,
  tableRows,
  columns,
  debouncedSearch,
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onRowClick,
  onRegister,
  onClosePanel,
  onEditSuccess,
  onConnectivityUpdate,
  modalityOptions,
  machines,
}: Readonly<{
  selectedMachine: Machine | null;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  columnsOpen: boolean;
  setColumnsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  columnsRef: React.RefObject<HTMLDivElement | null>;
  toggleableColumns: MachineColumnKey[];
  columnVisibility: Record<MachineColumnKey, boolean>;
  setColumnVisibility: React.Dispatch<React.SetStateAction<Record<MachineColumnKey, boolean>>>;
  loading: boolean;
  error: string | null;
  tableRows: MachineTableRow[];
  columns: TableColumn<MachineTableRow>[];
  debouncedSearch: string;
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onRowClick: (row: MachineTableRow) => void;
  onRegister: () => void;
  onClosePanel: () => void;
  onEditSuccess: (machine: Machine) => void;
  onConnectivityUpdate: (machine: Machine) => void;
  modalityOptions: { value: string; label: string }[];
  machines: Machine[];
}>) {
  return (
    <div className="machine-management-body">
      <div
        className={`machine-management-list-pane ${
          selectedMachine ? 'machine-management-list-pane--with-panel' : ''
        }`}
      >
        <div className="machine-management-toolbar">
          <div className="machine-management-search-wrap">
            <Search
              className="machine-management-search-icon"
              size={18}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            <input
              type="search"
              className="machine-management-search"
              placeholder="Search by machine, AE title, modality, location…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Search machines"
            />
          </div>
          <div
            className="machine-management-columns-wrap"
            ref={columnsRef}
          >
            <button
              type="button"
              className="machine-management-columns-btn"
              onClick={() => setColumnsOpen(prev => !prev)}
              aria-expanded={columnsOpen}
            >
              Columns
              <ChevronDown
                size={16}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
            {columnsOpen ? (
              <div
                className="machine-management-columns-menu"
                role="menu"
              >
                {toggleableColumns.map(key => (
                  <label
                    key={key}
                    className="machine-management-columns-item"
                  >
                    <input
                      type="checkbox"
                      checked={columnVisibility[key]}
                      onChange={() =>
                        setColumnVisibility(prev => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                    />
                    {MACHINE_COLUMN_LABELS[key]}
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="machine-management-table-wrap">
          <MachineImagingTableBody
            loading={loading}
            error={error}
            tableRows={tableRows}
            columns={columns}
            debouncedSearch={debouncedSearch}
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onRowClick={onRowClick}
            onRegister={onRegister}
          />
        </div>
      </div>

      {selectedMachine ? (
        <MachineEditPanel
          machine={selectedMachine}
          onClose={onClosePanel}
          onSuccess={onEditSuccess}
          onConnectivityUpdate={onConnectivityUpdate}
          modalityOptions={modalityOptions}
          existingMachines={machines}
        />
      ) : null}
    </div>
  );
}
